import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Star } from 'lucide-react';
import type { GoalDetail, ReviewGoalInput } from '@pms/shared';
import { reviewGoalSchema } from '@pms/shared';
import { goalsService } from '@/services/goals.service';
import { getErrorMessage } from '@/lib/errors';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { GoalProgressBar } from '@/components/goals/GoalProgressBar';

interface Props {
  goal: GoalDetail | null;
  onClose: () => void;
}

const RATINGS = [1, 2, 3, 4, 5];

export function ReviewGoalModal({ goal, onClose }: Props) {
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    register,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ReviewGoalInput>({
    resolver: zodResolver(reviewGoalSchema),
    defaultValues: { decision: 'approve', rating: null, comment: null },
  });

  useEffect(() => {
    if (goal) reset({ decision: 'approve', rating: null, comment: null });
  }, [goal, reset]);

  const decision = watch('decision');

  const mutation = useMutation({
    mutationFn: (data: ReviewGoalInput) => goalsService.reviewGoal(goal!.id, data),
    onSuccess: () => {
      toast.success(decision === 'approve' ? 'Goal approved' : 'Goal sent back');
      void queryClient.invalidateQueries({ queryKey: ['goals'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const busy = isSubmitting || mutation.isPending;

  return (
    <Dialog open={!!goal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Goal</DialogTitle>
          <DialogDescription className="line-clamp-2">
            {goal ? `${goal.title} — ${goal.owner_name ?? 'Unknown employee'}` : ''}
          </DialogDescription>
        </DialogHeader>

        {goal && (
          <form onSubmit={handleSubmit((data) => mutation.mutate(data))} className="space-y-4">
            <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
              <GoalProgressBar goal={goal} />
            </div>

            <div className="space-y-1">
              <Label>Decision</Label>
              <Controller
                control={control}
                name="decision"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approve">Approve — mark as achieved</SelectItem>
                      <SelectItem value="reject">Send back — reopen for more work</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {decision === 'approve' && (
              <div className="space-y-1">
                <Label>
                  Rating <span className="text-destructive">*</span>
                </Label>
                <Controller
                  control={control}
                  name="rating"
                  render={({ field }) => (
                    <div className="flex items-center gap-1">
                      {RATINGS.map((r) => (
                        <button
                          key={r}
                          type="button"
                          aria-label={`Rate ${r} of 5`}
                          onClick={() => field.onChange(r)}
                          className="rounded p-1 transition-colors hover:bg-muted"
                        >
                          <Star
                            className={cn(
                              'h-6 w-6',
                              (field.value ?? 0) >= r
                                ? 'fill-amber-400 text-amber-400'
                                : 'text-muted-foreground/40',
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  )}
                />
                {errors.rating && <p className="text-xs text-destructive">{errors.rating.message}</p>}
              </div>
            )}

            <div className="space-y-1">
              <Label htmlFor="review-comment">
                Comment {decision === 'reject' && <span className="text-destructive">*</span>}
              </Label>
              <Textarea
                id="review-comment"
                rows={4}
                placeholder={decision === 'approve'
                  ? 'Optional — what went well?'
                  : 'What still needs work before this can be approved?'}
                className={errors.comment ? 'border-destructive' : ''}
                {...register('comment')}
              />
              {errors.comment && <p className="text-xs text-destructive">{errors.comment.message}</p>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
                Cancel
              </Button>
              <Button type="submit" disabled={busy}>
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {decision === 'approve' ? 'Approve Goal' : 'Send Back'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
